import { Component, Input, OnInit } from '@angular/core';

import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { AccountService } from '../../../../../../services/account/account.service';
import { generateCsvDownload, getISO8601DateString, propExists } from '../../../../../../utils';
import { ScheduledEvent, ScheduledEventReservation } from '../../../../../../models/scheduled-event';
import { Customer, CustomerProfileDetails } from '../../../../../../models/customer';
import * as moment from 'moment';
import { getCustomerFieldValue } from '../../../../../../utils-models';

import { ProfileField } from '../../../../../../models/organization-config-enums';

const COLUMN_NAME_RESERVATION_ID = 'Reservation ID';
const COLUMN_NAME_CUSTOMER_ID = 'Customer ID';
const COLUMN_NAME_FIRST_NAME = 'First name';
const COLUMN_NAME_LAST_NAME = 'Last name';
const COLUMN_NAME_PREFERRED_NAME = 'Preferred name';
const COLUMN_NAME_BIRTHDAY = 'Birthday';
const COLUMN_NAME_AGE = 'Age';
const COLUMN_NAME_EMAIL_ADDRESS = 'Email address';
const COLUMN_NAME_TELEPHONE = 'Telephone';
const COLUMN_NAME_FEE_TYPE = 'Fee type';
const COLUMN_NAME_ADD_ONS = 'Add-ons';
const COLUMN_NAME_INVOICED_AMOUNT = 'Invoiced amount';
const COLUMN_NAME_AMOUNT_PAID = 'Amount paid';
const COLUMN_NAME_AMOUNT_REFUNDED = 'Amount refunded';
const COLUMN_NAME_GYM_STATUS = 'Gym status';
const COLUMN_NAME_HAS_CHECKED_IN = 'Has checked in?';
const COLUMN_NAME_BOOKED_BY_CUSTOMER_ID = 'Booked by customer ID';
const COLUMN_NAME_BOOKED_BY_CUSTOMER_FIRST_NAME = 'Booked by first name';
const COLUMN_NAME_BOOKED_BY_CUSTOMER_LAST_NAME = 'Booked by last name';
const COLUMN_NAME_BOOKED_BY_CUSTOMER_EMAIL_ADDRESS = 'Booked by email address';

@Component({
  selector: 'app-export-event-attendees-modal',
  templateUrl: './export-event-attendees-modal.component.html',
  styleUrls: ['./export-event-attendees-modal.component.css']
})
export class ExportEventAttendeesModalComponent implements OnInit {

  @Input() scheduledEvent: ScheduledEvent;
  @Input() customers: {[id: number]: Customer};
  @Input() customerProfileDetails: {[id: number]: CustomerProfileDetails};
  @Input() savedCallback: () => undefined;

  public genericColumnNames = [
    COLUMN_NAME_RESERVATION_ID,
    COLUMN_NAME_CUSTOMER_ID,
    COLUMN_NAME_FIRST_NAME,
    COLUMN_NAME_LAST_NAME,
    COLUMN_NAME_PREFERRED_NAME,
    COLUMN_NAME_BIRTHDAY,
    COLUMN_NAME_AGE,
    COLUMN_NAME_EMAIL_ADDRESS,
    COLUMN_NAME_TELEPHONE,
    COLUMN_NAME_FEE_TYPE,
    COLUMN_NAME_ADD_ONS,
    COLUMN_NAME_INVOICED_AMOUNT,
    COLUMN_NAME_AMOUNT_PAID,
    COLUMN_NAME_AMOUNT_REFUNDED,
    COLUMN_NAME_GYM_STATUS,
    COLUMN_NAME_HAS_CHECKED_IN,
    COLUMN_NAME_BOOKED_BY_CUSTOMER_ID,
    COLUMN_NAME_BOOKED_BY_CUSTOMER_FIRST_NAME,
    COLUMN_NAME_BOOKED_BY_CUSTOMER_LAST_NAME,
    COLUMN_NAME_BOOKED_BY_CUSTOMER_EMAIL_ADDRESS
  ];
  public attendeeQuestionColumnNames: string[] = [];
  public bookingQuestionColumnNames: string[] = [];

  public columnNames: string[] = [];

  public defaultDownloadFileName: string;
  public downloadFileName: string;
  // Whether each columnNames item has been selected for inclusion in the
  // download.
  public selectedColumns: {[key: string]: boolean};

  constructor(
    public accountService: AccountService,
    public activeModal: NgbActiveModal
  ) {}

  ngOnInit() {
    this.loadAttendeeQuestionColumnNames();
    this.loadBookingQuestionColumnNames();

    this.columnNames = [
      ...this.genericColumnNames,
      ...this.attendeeQuestionColumnNames,
      ...this.bookingQuestionColumnNames,
    ]

    this.defaultDownloadFileName = 'Event_Attendees_Export_' + getISO8601DateString(new Date()) + '.csv';
    this.downloadFileName = this.defaultDownloadFileName;

    this.selectedColumns = {};
    for (const columnName of this.columnNames) {
      this.selectedColumns[columnName] = false;
    }
  }

  public noColumnsAreSelected() {
    for (const columnName of this.columnNames) {
      if (this.selectedColumns[columnName]){
        return false;
      }
    }

    return true;
  }

  public allColumnsAreSelected() {
    for (const columnName of this.columnNames) {
      if (!this.selectedColumns[columnName]){
        return false;
      }
    }

    return true;
  }

  public setAllColumnsSelected(isSelected: boolean) {
    this.columnNames.forEach(columnName => {
      this.selectedColumns[columnName] = isSelected;
    });
  }

  public handleSubmit() {
    if (!this.downloadFileName) {
      this.downloadFileName = this.defaultDownloadFileName;
    }

    const selectedColumnsList = this.getSelectedColumnsList();
    const content = [selectedColumnsList];
    for (const reservation of this.scheduledEvent.reservations) {
      if (reservation.is_cancelled) { continue; }
      const thisContentRow: string[] = [];
      for (const selectedColumnName of selectedColumnsList) {
        thisContentRow.push(
          this.getContentItem(reservation, selectedColumnName)
        );
      }
      content.push(thisContentRow);
    }

    generateCsvDownload(content, this.downloadFileName);
  }

  private loadAttendeeQuestionColumnNames() {
    const questionNamesSet = new Set<string>();
    for (const reservation of this.scheduledEvent.reservations) {
      for (const questionName of Object.keys(reservation.question_responses)) {
        questionNamesSet.add(questionName);
      }
    }
    const questionNamesArray = [...questionNamesSet];
    questionNamesArray.sort();
    for (const questionName of questionNamesArray) {
      this.attendeeQuestionColumnNames.push(questionName);
    }
  }

  private loadBookingQuestionColumnNames() {
    const questionNamesSet = new Set<string>();
    for (const reservation of this.scheduledEvent.reservations) {
      for (const questionName of Object.keys(reservation.booking_question_responses)) {
        questionNamesSet.add(questionName);
      }
    }
    const questionNamesArray = [...questionNamesSet];
    questionNamesArray.sort();
    for (const questionName of questionNamesArray) {
      this.bookingQuestionColumnNames.push(questionName);
    }
  }

  /**
   * Get the selected columns as a list.
   */
  private getSelectedColumnsList(): string[] {
    const selectedColumnsList: string[] = [];
    for (const columnName of this.columnNames) {
      if (this.selectedColumns[columnName]) {
        selectedColumnsList.push(columnName);
      }
    }
    return selectedColumnsList;
  }

  private getContentItem(reservation: ScheduledEventReservation,
                         selectedColumnName: string): string {
    switch (selectedColumnName) {
      case COLUMN_NAME_RESERVATION_ID:
        return reservation.reservation_id.toString();
      case COLUMN_NAME_CUSTOMER_ID:
        if (!propExists(reservation.customer_id)) {
          return null;
        }
        return reservation.customer_id.toString();
      case COLUMN_NAME_FIRST_NAME:
        return reservation.first_name;
      case COLUMN_NAME_LAST_NAME:
        return reservation.last_name;
      case COLUMN_NAME_PREFERRED_NAME:
        return reservation.preferred_name;
      case COLUMN_NAME_BIRTHDAY:
        return reservation.birthday;
      case COLUMN_NAME_AGE:
        const birthday = moment(reservation.birthday, 'YYYY-MM-DD');
        return moment().diff(birthday, 'years').toString();
      case COLUMN_NAME_EMAIL_ADDRESS:
        return reservation.email;
      case COLUMN_NAME_TELEPHONE:
        return reservation.telephone;
      case COLUMN_NAME_FEE_TYPE:
        return reservation.payment_fee_name;
      case COLUMN_NAME_ADD_ONS:
        return (reservation.add_on_names_display || reservation.add_on_names).join(',');
      // This participant's share of their booking's money (PRO-1491). Emitted
      // as a plain two-place number with no currency symbol, so a spreadsheet
      // sums the column without cleanup.
      case COLUMN_NAME_INVOICED_AMOUNT:
        return reservation.invoiced_amount;
      case COLUMN_NAME_AMOUNT_PAID:
        return reservation.amount_paid;
      case COLUMN_NAME_AMOUNT_REFUNDED:
        return reservation.amount_refunded;
      case COLUMN_NAME_GYM_STATUS:
        const c = this.customers[reservation.customer_id];
        const cPD = this.customerProfileDetails[reservation.customer_id];
        if (!propExists(c) || !propExists(cPD)) { return null; }
        return getCustomerFieldValue(
          ProfileField.gym_status,
          c,
          cPD,
          false
        );
      case COLUMN_NAME_HAS_CHECKED_IN:
        return reservation.has_checked_in ? 'Yes' : 'No';
      case COLUMN_NAME_BOOKED_BY_CUSTOMER_ID:
        if (!propExists(reservation.booked_by_id)) { return null; }
        return reservation.booked_by_id.toString();
      case COLUMN_NAME_BOOKED_BY_CUSTOMER_FIRST_NAME:
        if (!propExists(reservation.booked_by_first_name)) { return null; }
        return reservation.booked_by_first_name;
      case COLUMN_NAME_BOOKED_BY_CUSTOMER_LAST_NAME:
        if (!propExists(reservation.booked_by_last_name)) { return null; }
        return reservation.booked_by_last_name;
      case COLUMN_NAME_BOOKED_BY_CUSTOMER_EMAIL_ADDRESS:
        return reservation.booked_by_email;
      default:
        let questionResponse = reservation.question_responses[selectedColumnName];
        if (questionResponse === undefined) {
          questionResponse = reservation.booking_question_responses[selectedColumnName];
        }
        if (questionResponse === undefined) {
          return null;
        }
        if (Array.isArray(questionResponse)) {
          questionResponse = questionResponse.join(',');
        }
        return questionResponse;
    }
  }

}
